import { FC } from "react";

import { Box } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import Icon from "@components/Icon";
import Link from "@components/Link";
import { useMessage } from "@components/Message";
import TextInput, { Textarea } from "@components/TextInput";
import { Mobile } from "@lib/media";
import { useRouter } from "@lib/router";
import { AddressInputs } from "@modules/address-search";
import { ROUTES, LOCALE } from "@utils/constants";

import { OrderFields, ORDER_MESSAGE_ZONE, ORDER_FIELDS_MAX_LENGTHS } from "../../constants";
import useOrderData from "../../context/useOrderData";
import useOrderFormik from "../../hooks/useOrderFormik";
import SectionTitle from "../common/SectionTitle";
import TodaysPickup from "./TodaysPickup";
import getIsDeliveryCollectionRequest from "../../utils/getIsDeliveryCollectionRequest";

const SenderAddress: FC = () => {
  const { t } = useTranslation();

  const { values } = useOrderFormik();

  const setMessage = useMessage();
  const {
    staticData: { countriesFrom },
  } = useOrderData();

  const { defaultLocale } = useRouter();

  const startingCountry = values[OrderFields.STARTING_COUNTRY];

  const handleAddressSearchError = () => {
    setMessage({
      message: t("order-message-failed_address_search"),
      props: {
        variant: "error",
        icon: <Icon icon="warning" size="1.45rem" />,
        canClose: true,
        padding: "0.45rem 0.75rem",
      },
      zone: ORDER_MESSAGE_ZONE,
    });
  };

  const isCollectionRequest = getIsDeliveryCollectionRequest(defaultLocale!, startingCountry || undefined)

  return (
    <FullWidthVStack spacing="1rem">
      <SectionTitle>{t("order-sender-address_details")}</SectionTitle>
      <Box>
        <TextInput
          name={OrderFields.STARTING_COUNTRY_NAME}
          isReadOnly
          rightAddon={
            (defaultLocale !== LOCALE.LT_LOCALE || countriesFrom.length > 1) && (
              <Link label={t("order-field-edit")} href={ROUTES.ORDER} id="order-sender-country_edit" />
            )
          }
          id="order-sender-country"
        />
      </Box>
      <AddressInputs
        id="order-sender-address"
        countryCode={startingCountry!}
        streetFieldName={OrderFields.SENDER_STREET}
        houseNoFieldName={OrderFields.SENDER_HOUSE_NO}
        flatNoFieldName={OrderFields.SENDER_FLAT_NO}
        cityFieldName={OrderFields.SENDER_CITY}
        zipCodeFieldName={OrderFields.SENDER_ZIP_CODE}
        onError={handleAddressSearchError}
        isAddressSelectedFromAddressBook={false}
      />
      <Box>
        <Textarea
          name={OrderFields.SENDER_COMMENTS}
          label={`${t("order-sender-sender_comments_for_courier")} ${t("common-optional")}`}
          id="order-sender-courier_comments"
          maxLength={isCollectionRequest ? ORDER_FIELDS_MAX_LENGTHS.senderCollectionRequest : ORDER_FIELDS_MAX_LENGTHS.senderComment}
        />
      </Box>
      <Mobile>
        <TodaysPickup />
      </Mobile>
    </FullWidthVStack>
  );
};
export default SenderAddress;
